import React from "react";
import {
  FiAlertTriangle, FiBookOpen, FiCreditCard, FiMessageCircle,
  FiServer, FiShield, FiTool, FiZap
} from "react-icons/fi";
import Layout from "../../components/layout/Layout";


export default function Support(props) {
  const {
    productName = "Predixio",
    contactEmail = "support@predixio.com",
    statusUrl = "https://status.predixio.com",
    communityUrl = "https://community.predixio.com",
    docsUrl = "https://docs.predixio.com",
  } = props;

  const color = {
    pageBg: "#f7f9fc",
    ink: "#0f172a",
    inkSub: "#475569",
    brand: "#2563eb",
    brandAlt: "#1d4ed8",
    border: "rgba(15, 23, 42, .08)",
    cardBg: "#ffffff",
    goodBg: "rgba(16,185,129,.12)",
    goodBorder: "rgba(16,185,129,.35)",
  };

  const ui = {
    page: { background: color.pageBg, minHeight: "100vh" },
    container: { maxWidth: 1400, margin: "0 auto", padding: "28px 16px" },

    heroWrap: {
      position: "relative",
      padding: "70px 16px 70px",
      background:
        "radial-gradient(1200px 300px at 50% -30%, rgba(59,130,246,0.25), transparent 60%), linear-gradient(180deg, #0b1020 0%, #0b1020 40%, #0c1428 100%)",
      color: "white",
      overflow: "hidden",
      borderBottom: "1px solid rgba(255,255,255,0.06)",
    },
    heroInner: { maxWidth: 1350, margin: "0 auto" },
    badge: {
      display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12,
      color: "#93c5fd", background: "rgba(147,197,253,.10)", border: "1px solid rgba(147,197,253,.30)",
      padding: "6px 12px", borderRadius: 999,
    },
    heroTitle: { margin: "14px 0 10px", fontSize: 36, lineHeight: 1.15, color: "white", fontWeight: 900, letterSpacing: "-0.02em" },
    heroSub: { margin: 0, color: "rgba(255,255,255,.85)" },

    searchRow: { marginTop: 20, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
    searchBox: {
      flex: "1 1 520px", display: "flex", alignItems: "center", gap: 10,
      background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.24)",
      borderRadius: 12, padding: "10px 12px", backdropFilter: "blur(6px)",
    },
    input: { flex: 1, height: 38, background: "transparent", border: "none", outline: "none", color: "white", fontSize: 14 },
    btnPrimary: {
      height: 60, padding: "0 16px",
      background: `linear-gradient(180deg, ${color.brand}, ${color.brandAlt})`,
      color: "white", border: "1px solid rgba(37,99,235,.6)", borderRadius: 10, fontWeight: 700, cursor: "pointer",
    },

    section: { padding: "28px 16px" },

    tile: {
      background: color.cardBg, border: `1px solid ${color.border}`, borderRadius: 14,
      padding: 16, color: color.ink, transition: "transform .18s ease, box-shadow .18s ease",
      boxShadow: "0 1px 2px rgba(0,0,0,.04)", cursor: "pointer",
    },
    tileHover: { transform: "translateY(-3px)", boxShadow: "0 10px 24px rgba(2,6,23,.10)" },
    tileTitle: { margin: "8px 0 4px", fontWeight: 800, fontSize: 16 },
    tileSub: { margin: 0, color: color.inkSub, fontSize: 13 },

    card: { background: color.cardBg, border: `1px solid ${color.border}`, borderRadius: 16, padding: 18, color: color.ink, boxShadow: "0 1px 2px rgba(0,0,0,.04)" },
    cardHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 12, flexWrap: "wrap" },
    cardTitle: { margin: 0, fontWeight: 900, letterSpacing: "-0.02em", fontSize: 18 },
    link: { color: color.brand, textDecoration: "none", fontSize: 13, fontWeight: 600 },

    accItem: { border: `1px solid ${color.border}`, borderRadius: 12, overflow: "hidden", background: "#fff" },
    accHead: {
      width: "100%", textAlign: "left", background: "#fafafa", border: "none", outline: "none",
      color: color.ink, padding: "12px 14px", fontWeight: 700, cursor: "pointer",
      display: "flex", alignItems: "center", justifyContent: "space-between",
    },
    accPanel: { padding: "12px 14px", color: color.inkSub, lineHeight: 1.6, borderTop: `1px solid ${color.border}`, background: "#fff" },

    label: { fontSize: 12, color: color.inkSub, marginBottom: 6, fontWeight: 600 },
    field: { width: "100%", height: 42, background: "#fff", border: `1px solid ${color.border}`, borderRadius: 10, color: color.ink, padding: "0 12px", outline: "none" },
    textarea: { width: "100%", minHeight: 120, background: "#fff", border: `1px solid ${color.border}`, borderRadius: 12, color: color.ink, padding: 12, outline: "none", resize: "vertical" },
    btnGhost: { height: 42, padding: "0 14px", background: "transparent", color: color.ink, border: `1px solid ${color.border}`, borderRadius: 10, fontWeight: 700, cursor: "pointer" },

    kpi: { background: "#fff", border: `1px solid ${color.border}`, borderRadius: 12, padding: 12, textAlign: "center" },
    kpiNumber: { fontSize: 18, fontWeight: 900, color: color.ink },
    kpiLabel: { margin: 0, color: color.inkSub, fontSize: 12 },
  };

  const Hover = ({ style, children }) => {
    const [h, setH] = React.useState(false);
    return (
      <div
        style={{ ...style, ...(h ? ui.tileHover : null) }}
        onMouseEnter={() => setH(true)}
        onMouseLeave={() => setH(false)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setH((v) => !v)}
      >
        {children}
      </div>
    );
  };

  const [q, setQ] = React.useState("");
  const onSearch = (e) => {
    e.preventDefault();
    const lower = q.toLowerCase();
    const anchor =
      lower.includes("billing") ? "#faq-billing" :
        lower.includes("api") ? "#faq-api" :
          lower.includes("account") ? "#faq-account" :
            lower.includes("prediction") ? "#faq-predictions" :
              "#faq";
    document.querySelector(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const [open, setOpen] = React.useState({});
  const toggle = (id) => setOpen((s) => ({ ...s, [id]: !s[id] }));

  const tiles = [
    { icon: <FiTool size={22} />, title: "Report an Issue", sub: "App bugs, API errors, login problems" },
    { icon: <FiCreditCard size={22} />, title: "Billing & Plans", sub: "Invoices, upgrades, refunds, VAT" },
    { icon: <FiZap size={22} />, title: "Predictions & Models", sub: "Accuracy, markets, data sources" },
    { icon: <FiBookOpen size={22} />, title: "API & Developers", sub: "Tokens, rate limits, endpoints" },
  ];

  const faqs = [
    {
      id: "faq-account", group: "Account & Security", items: [
        ["How do I reset my password?", "Use “Forgot password?” on the sign-in page. If you’re locked out, contact support so we can verify ownership and assist."],
        ["Can I enable 2FA for extra security?", "Yes. Settings → Security → Two-Factor Authentication supports TOTP apps (e.g., Authy, Google Authenticator)."],
        ["How do I change my email address?", "Go to Settings → Profile. We’ll send a verification link to the new email to finalize the change."],
      ]
    },
    {
      id: "faq-billing", group: "Billing & Subscription", items: [
        ["What payment methods do you accept?", "Major credit/debit cards and (where available) Apple Pay / Google Pay. Annual enterprise invoicing is available on request."],
        ["How do refunds work?", "Unless required by law or stated otherwise, fees are non-refundable. See Terms → Subscriptions & Refunds for details."],
        ["Can I change plans mid-cycle?", "Upgrades apply immediately (prorated). Downgrades take effect on your next billing date."],
      ]
    },
    {
      id: "faq-predictions", group: "Predictions & Data", items: [
        ["How often are models updated?", "Core models retrain weekly; weights and calibrations may refresh more frequently based on data flow."],
        ["Do you guarantee betting outcomes?", "No. Predixio is informational only. Wager responsibly and only what you can afford to lose."],
        ["What data feeds do you use?", "We aggregate multiple reputable football data sources and apply internal QA and latency smoothing."],
      ]
    },
    {
      id: "faq-api", group: "API & Rate Limits", items: [
        ["Where do I get an API token?", "Developer → API Access in your account. Keep tokens secret; rotate if exposed."],
        ["Why am I getting 429 rate-limit errors?", "Add caching, request coalescing, and exponential backoff. Avoid bursty concurrency."],
        ["Do you have a sandbox?", "Yes—use the “demo” header or sandbox base URL in the docs to test without live quota."],
      ]
    },
  ];

  return (
    <Layout headerStyle={1} footerStyle={1} breadcrumbTitle="Support">
      <style>{`
        /* Clamp horizontal overflow just on this page */
        .sp-page, .sp-container, .sp-main { box-sizing: border-box; max-width: 100%; overflow-x: clip; }

        /* --- Grid helpers (12-col) --- */
        .sp-grid { display: grid; gap: 16px; grid-template-columns: repeat(12, minmax(0,1fr)); }
        .sp-col-12 { grid-column: span 12 / span 12; }
        .sp-col-8  { grid-column: span 8  / span 8; }
        .sp-col-4  { grid-column: span 4  / span 4; }
        .sp-col-6  { grid-column: span 6  / span 6; }
        .sp-col-3  { grid-column: span 3  / span 3; }

        /* Content two-column: left (FAQ) | right (contact) */
        .sp-content { display: grid; gap: 16px; grid-template-columns: 2fr 1fr; }
        .sp-left   { min-width: 0; } /* allow shrinking */
        .sp-right  { min-width: 0; }

        /* Tiles grid default: 4 columns */
        .sp-tiles { display: grid; gap: 16px; grid-template-columns: repeat(4, minmax(0,1fr)); }

        /* KPI grid default: 2 columns */
        .sp-kpis { display: grid; gap: 10px; grid-template-columns: repeat(2, minmax(0,1fr)); }

        /* Make sure images/tables/long tokens never overflow */
        .sp-main img, .sp-main table, .sp-main code { max-width: 100%; }
        .sp-wrap-x { overflow-x: auto; }

        /* Tablet */
        @media (max-width: 1024px) {
          .sp-content { grid-template-columns: 1.5fr 1fr; }
          .sp-tiles { grid-template-columns: repeat(2, minmax(0,1fr)); }
        }

        /* Mobile */
        @media (max-width: 768px) {
          .sp-content { grid-template-columns: 1fr; }
          .sp-tiles   { grid-template-columns: 1fr; }
          .sp-kpis    { grid-template-columns: 1fr 1fr; } /* still two-up but flexible */
          .sp-search-btn { width: 100%; height: 48px; }   /* hero search button full width */
        }
      `}</style>

      <section style={ui.heroWrap}>
        <div style={ui.heroInner}>
          <span style={ui.badge}><FiShield /> We’re here to help</span>
          <h1 style={ui.heroTitle}>{productName} Support</h1>
          <p style={ui.heroSub}>
            Search help, browse topics, or contact us. Average first response under 2 hours on business days.
          </p>

          <form onSubmit={onSearch} style={ui.searchRow} role="search" aria-label="Support search">
            <div style={ui.searchBox}>
              <FiMessageCircle aria-hidden />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search help (e.g., billing, API, predictions)…"
                style={ui.input}
                aria-label="Search support articles"
              />
            </div>
            <button type="submit" style={ui.btnPrimary} className="sp-search-btn">Search</button>
          </form>
        </div>
      </section>

      <section style={{ ...ui.section, backgroundColor: "#f0f4f8" }}>
        <div style={ui.container}>
          <div className="sp-tiles">
            {tiles.map((t, i) => (
              <div key={i}>
                <Hover style={ui.tile}>
                  <div>{t.icon}</div>
                  <h4 style={ui.tileTitle}>{t.title}</h4>
                  <p style={ui.tileSub}>{t.sub}</p>
                </Hover>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ ...ui.section, backgroundColor: "#f5f5f5" }}>
        <div style={ui.container}>
          <div className="sp-content sp-main">
            <div className="sp-left">
              <div style={ui.card}>
                <div style={ui.cardHeader}>
                  <h3 id="faq" style={ui.cardTitle}>Popular help topics</h3>
                  <a href={docsUrl} target="_blank" rel="noreferrer" style={ui.link}>
                    <FiBookOpen style={{ verticalAlign: "text-bottom" }} /> Open documentation
                  </a>
                </div>

                <div style={{ display: "grid", gap: 12 }}>
                  {faqs.map((group) => (
                    <div key={group.id} id={group.id} style={{ display: "grid", gap: 10 }}>
                      <h4 style={{ margin: "12px 2px 6px", fontWeight: 900, color: color.ink }}>{group.group}</h4>
                      {group.items.map(([question, answer], idx) => {
                        const id = `${group.id}-${idx}`;
                        const isOpen = !!open[id];
                        return (
                          <div key={id} style={ui.accItem}>
                            <button
                              style={ui.accHead}
                              aria-expanded={isOpen}
                              aria-controls={`${id}-panel`}
                              onClick={() => toggle(id)}
                            >
                              <span>{question}</span>
                              <span aria-hidden>{isOpen ? "▾" : "▸"}</span>
                            </button>
                            {isOpen && <div id={`${id}-panel`} style={ui.accPanel}>{answer}</div>}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="sp-right">
              <div style={{ ...ui.card, marginBottom: 16 }}>
                <div style={ui.cardHeader}>
                  <h3 style={ui.cardTitle}>Contact us</h3>
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                  <a href={`mailto:${contactEmail}`} style={ui.link}><FiMessageCircle style={{ verticalAlign: "text-bottom" }} /> Email support</a>
                  <a href="#live-chat" style={ui.link}><FiMessageCircle style={{ verticalAlign: "text-bottom" }} /> Live chat (Beta)</a>
                  <a href={communityUrl} target="_blank" rel="noreferrer" style={ui.link}><FiServer style={{ verticalAlign: "text-bottom" }} /> Community & Discord</a>
                  <a href={statusUrl} target="_blank" rel="noreferrer" style={ui.link}><FiServer style={{ verticalAlign: "text-bottom" }} /> Status & uptime</a>
                </div>
              </div>

              <div className="sp-kpis">
                <div style={ui.kpi}><div style={ui.kpiNumber}>~1h</div><p style={ui.kpiLabel}>Avg. first reply</p></div>
                <div style={ui.kpi}><div style={ui.kpiNumber}>96%</div><p style={ui.kpiLabel}>CSAT (30d)</p></div>
                <div style={ui.kpi}><div style={ui.kpiNumber}>24/7</div><p style={ui.kpiLabel}>Monitoring</p></div>
                <div style={ui.kpi}><div style={ui.kpiNumber}>99.95%</div><p style={ui.kpiLabel}>Uptime SLO</p></div>
              </div>

              <div style={{ ...ui.card, marginTop: 16, background: color.goodBg, borderColor: color.goodBorder }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#065f46", fontWeight: 600 }}>
                  <FiAlertTriangle /> No active incidents reported.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
